import React, { useState, useEffect } from 'react';
import { usersAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import './DriverManagement.css';

function DriverManagement() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    loadDrivers(currentPage);
  }, [currentPage]);

  const loadDrivers = async (page) => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll('driver', page, 25);
      setDrivers(response.data.users);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error loading drivers:', error);
      alert('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const handleDriverClick = (driverId) => {
    navigate(`/admin/drivers/${driverId}`);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first, last, and pages around current
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return (
      <div className="pagination">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn"
        >
          Previous
        </button>

        {pages.map((page, index) => (
          page === '...' ? (
            <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
          ) : (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
            >
              {page}
            </button>
          )
        ))}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-btn"
        >
          Next
        </button>
      </div>
    );
  };

  if (loading && currentPage === 1) {
    return <div className="loading">Loading drivers...</div>;
  }

  return (
    <div className="page driver-management-page">
      <div className="page-header">
        <h1 className="page-title">Driver Management</h1>
        <p className="driver-count">{total} total driver{total !== 1 ? 's' : ''}</p>
      </div>

      {drivers.length === 0 ? (
        <div className="card">
          <p>No drivers found. Create drivers from the Admin Dashboard.</p>
        </div>
      ) : (
        <>
          <div className="drivers-grid">
            {drivers.map((driver) => (
              <div
                key={driver.id}
                className="driver-card"
              >
                <div className="driver-header">
                  <div className="driver-avatar">
                    {driver.first_name[0]}{driver.last_name[0]}
                  </div>
                  <div className="driver-info">
                    <h3>{driver.first_name} {driver.last_name}</h3>
                    <p className="driver-email">{driver.email}</p>
                  </div>
                </div>

                <div className="driver-details">
                  {driver.phone && (
                    <div className="driver-card-detail">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{driver.phone}</span>
                    </div>
                  )}
                  {driver.vehicle_info && (
                    <div className="driver-card-detail">
                      <span className="detail-label">Vehicle:</span>
                      <span className="detail-value">{driver.vehicle_info}</span>
                    </div>
                  )}
                  {driver.license_number && (
                    <div className="driver-card-detail">
                      <span className="detail-label">License:</span>
                      <span className="detail-value">{driver.license_number}</span>
                    </div>
                  )}
                  <div className="driver-card-detail">
                    <span className="detail-label">Status:</span>
                    <span className={`status-badge ${driver.is_active ? 'active' : 'inactive'}`}>
                      {driver.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="driver-card-detail">
                    <span className="detail-label">Approval:</span>
                    <span className={`approval-badge ${driver.approval_status}`}>
                      {driver.approval_status === 'pending' && '⏳ Pending'}
                      {driver.approval_status === 'approved' && '✓ Approved'}
                      {driver.approval_status === 'denied' && '✗ Denied'}
                    </span>
                  </div>
                </div>

                <div className="driver-footer">
                  <span
                    className="view-profile"
                    onClick={() => handleDriverClick(driver.id)}
                  >
                    View Profile →
                  </span>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination-container">
              {renderPagination()}
              <p className="pagination-info">
                Page {currentPage} of {totalPages}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DriverManagement;
